/**
 * Pending-attachments state for the chat composer.
 *
 * The composer tracks files the user has picked but not yet sent. Each
 * entry holds a stable client-side id (used for chip remove and the
 * eventual `attachment_ids` payload on send) plus a status that
 * reflects the upload lifecycle. The store is intentionally tiny — the
 * heavy lifting (upload, delete) lives in the api client; this just
 * accumulates the results.
 *
 * Per `feedback_solid_create_resource_pitfalls.md`: reset is keyed to
 * user actions (send-success, new-thread), NOT to async source changes
 * inside an effect.
 */
import { type Accessor, createSignal } from "solid-js";

export type PendingStatus = "uploading" | "ready" | "failed";

export interface PendingAttachment {
  /** Stable client-side id for chip lifecycle. Equal to the server
   *  `AttachmentResponse.id` once `status === "ready"`; before that, a
   *  local `tmp-…` token so the chip can render during upload. */
  id: string;
  filename: string;
  mime: string;
  /** May be 0 until upload completes — `File.size` is filled in by the
   *  picker so we can show a size hint immediately. */
  bytes: number;
  status: PendingStatus;
  /** Only set when `status === "failed"`. */
  error?: string;
}

export interface AttachmentsStore {
  list: Accessor<readonly PendingAttachment[]>;
  /** UUIDs of all `ready` attachments — exactly what goes into
   *  `ChatRequest.attachment_ids` on send. */
  readyIds: Accessor<string[]>;
  /** True while any chip is mid-upload. Used to disable send + picker. */
  isUploading: Accessor<boolean>;
  add: (a: PendingAttachment) => void;
  /** Replace the entry with matching `oldId`. Used to swap a `tmp-…` id
   *  for the real `AttachmentResponse.id` once upload completes. */
  replace: (oldId: string, next: PendingAttachment) => void;
  patch: (id: string, fields: Partial<PendingAttachment>) => void;
  remove: (id: string) => void;
  reset: () => void;
}

export function createAttachmentsStore(): AttachmentsStore {
  const [list, setList] = createSignal<readonly PendingAttachment[]>([]);

  const readyIds = () =>
    list()
      .filter((a) => a.status === "ready")
      .map((a) => a.id);

  const isUploading = () => list().some((a) => a.status === "uploading");

  const add = (a: PendingAttachment) => setList((arr) => [...arr, a]);

  const replace = (oldId: string, next: PendingAttachment) =>
    setList((arr) => arr.map((a) => (a.id === oldId ? next : a)));

  const patch = (id: string, fields: Partial<PendingAttachment>) =>
    setList((arr) => arr.map((a) => (a.id === id ? { ...a, ...fields } : a)));

  const remove = (id: string) =>
    setList((arr) => arr.filter((a) => a.id !== id));

  const reset = () => setList([]);

  return { list, readyIds, isUploading, add, replace, patch, remove, reset };
}

export function newTempId(): string {
  // Date.now() + random keeps ids unique even if the user picks two
  // files in the same millisecond. Crypto-grade not needed; this id is
  // local until the BE assigns the real UUID.
  return `tmp-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}
